import { Organisation, Permission } from 'src/app/interfaces/masterpass/policy';
import { AuthState, deriveKind, emptyAuthState, PermissionMap } from './auth.state';
import {
  COLLAB_ORG_KEY,
  PERMISSION_KEY,
  PROJECT_KEY,
  readJson,
  readRaw,
  TOKEN_KEY,
  USER_KEY,
  USER_ORG_KEY,
} from './auth.storage-keys';

/**
 * Rebuilds the session from localStorage.
 *
 * Passed to provideStore as an `initialState` FACTORY, so NgRx invokes it while
 * the root injector is being constructed — strictly before any APP_INITIALIZER,
 * before AppComponent bootstraps, and before the router activates a route. That
 * ordering is what lets the interceptor and guard read the token synchronously:
 * by the time anything can ask, the store already holds the answer.
 *
 * It must never throw. readJson swallows parse errors for that reason; a throw
 * here would kill the root injector and render nothing at all.
 */
export function hydrateAuthState(): AuthState {
  const user = readJson<any>(USER_KEY);

  // Stored JSON.stringify'd, so parsing yields the bare token. Anything that is
  // not a non-empty string (an old `{}`, a stray number) is no session at all.
  const token = readJson<unknown>(TOKEN_KEY);
  const permissions = readJson<PermissionMap | Permission[]>(PERMISSION_KEY);

  return {
    kind: deriveKind(user),
    token: typeof token === 'string' && token.length > 0 ? token : null,
    user: user ?? null,
    organisation: readJson<Organisation>(USER_ORG_KEY) ?? null,
    permissions: permissions ?? emptyAuthState.permissions,
    collabOrgId: readRaw(COLLAB_ORG_KEY),
    currentProject: readJson<any>(PROJECT_KEY) ?? null,
    permissionsStatus: permissions ? 'loaded' : 'idle',
  };
}
